'use client';

import BingUETScript from 'app/components/Common/BingUETScript';
import ClarityScript from 'app/components/Common/ClarityScript';
import FBPixelScript from 'app/components/Common/FBPixelScript';
import KlaviyoScript from 'app/components/Common/KlaviyoScript';
import useIdleLoading from 'app/hooks/useIdleLoading';
import { useSearchParams, usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import React, { FC, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { SUPPORTED_LANGUAGES } from 'app/locales/i18n';

interface RenderGAProps {
	uid: string;
	imid: string;
	reduxLng: string;
}

const DynamicGoogleAnalytics = dynamic(() => import('@next/third-parties/google').then((mod) => mod.GoogleAnalytics), {
	ssr: false,
});

const RenderGA: FC<RenderGAProps> = ({ uid, imid, reduxLng }) => {
	const isIdle = useIdleLoading();
	const [isGALoaded, setIsGALoaded] = useState(false);
	const params = useSearchParams();
	const pathname = usePathname();

	const urlLng = params?.get('lng') as string;
	const pathLng = pathname?.split('/')[1];
	const detectedPathLng = pathLng && SUPPORTED_LANGUAGES.includes(pathLng) ? pathLng : null;

	// Fallback: URL param → Pathname segment → Redux state → 'en'
	const siteLanguage = urlLng || detectedPathLng || reduxLng || 'en';
	const hasLoadedGA = useRef(false);

	useEffect(() => {
		if (isIdle && !hasLoadedGA.current) {
			let retries = 0;
			const MAX_RETRIES = 50; // Wait up to 5 seconds

			const checkGtagAndFireEvent = () => {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					setIsGALoaded(true);
				} else if (retries < MAX_RETRIES) {
					retries++;
					setTimeout(checkGtagAndFireEvent, 100);
				}
			};

			checkGtagAndFireEvent();
		}
	}, [isIdle]);

	useEffect(() => {
		if (isGALoaded && typeof window !== 'undefined' && typeof window.gtag === 'function') {
			if (uid && imid) {
				window.gtag('set', 'user_id', uid + imid);
			}
			window.gtag('set', 'user_properties', {
				site_language: siteLanguage,
			});
		}
	}, [isGALoaded, uid, imid, siteLanguage]);

	useEffect(() => {
		if (isGALoaded && !hasLoadedGA.current) {
			const fireEvent = () => {
				hasLoadedGA.current = true;
			};

			if (window.requestIdleCallback) {
				window.requestIdleCallback(fireEvent);
			} else {
				setTimeout(fireEvent, 0);
			}
		}
	}, [isGALoaded]);

	return (
		isIdle && (
			<>
				<DynamicGoogleAnalytics dataLayerName="dataLayer" gaId={process.env.NEXT_PUBLIC_GA_ID as string} />
				<ClarityScript clarityId={process.env.NEXT_PUBLIC_CLARITY_ID as string} />
				<FBPixelScript pixelId="363719008945777" />
				<BingUETScript tagId="148000929" />
				<KlaviyoScript companyId="RPT2kL" />
			</>
		)
	);
};

const mapStateToProps = (state: any) => ({
	uid: state.base.uid,
	imid: state.base.imid,
	reduxLng: state.base.lng,
});

export default connect(mapStateToProps)(React.memo(RenderGA));
